import { axiosClient } from '../../../../library/Service/axiosClient';

const MENTORSHIP_BASE = `${process.env.REACT_APP_COMMON_API}/mentorship`;

export type MenteeLookingForOption = {
    slug: string;
    label: string;
    description: string;
};

export type MenteeTrackOption = {
    id: number;
    name: string | null;
};

export type MenteeRegistrationIdentity = {
    picture: string | null;
    programName: string | null;
    batchYear: number | null;
    campusName?: string | null;
};

export type MenteeRegistrationPayload = {
    status: 'DRAFT' | 'SUBMITTED' | string;
    immutable: boolean;
    menteeMode: string | null;
    cohortId: number | null;
    campusId: number | null;
    menteePoolMemberId: number | null;
    identity: MenteeRegistrationIdentity;
    linkedinUrl: string | null;
    lookingFor: string[];
    lookingForOptions: MenteeLookingForOption[];
    freeText: string | null;
    trackIds: number[];
    tracks: MenteeTrackOption[];
    submittedAt: string | null;
};

export type MenteeRegistrationWriteBody = {
    lookingFor: string[];
    trackIds: number[];
    freeText?: string | null;
    linkedinUrl?: string | null;
};

export type MenteeLanding =
    | 'dashboard'
    | 'registration_awaiting_pairing'
    | 'registration_form'
    | string;

export type MenteeDashboardIdentity = {
    name: string | null;
    picture: string | null;
    programName: string | null;
    batchYear: number | null;
    campusName: string | null;
    company: string | null;
    designation: string | null;
};

export type MenteeDashboardMentor =
    | {
          status?: undefined;
          pairId: number | null;
          mentorUuid: string | null;
          name: string | null;
          photo: string | null;
          company: string | null;
          designation: string | null;
      }
    | {
          status: 'yet_to_be_assigned';
          label: string;
      };

export type MenteeChecklistStep = {
    id: string;
    label: string;
    state: string;
    bookEnabled?: boolean;
    collector?: string;
};

export type MenteeDashboardPayload = {
    landing: MenteeLanding;
    registrationComplete: boolean;
    awaitingPairingNote: string | null;
    menteeMode: string | null;
    cohort: {
        id: number | null;
        name: string | null;
        startDate: string | null;
        endDate: string | null;
    } | null;
    identity: MenteeDashboardIdentity;
    matchedDate: string | null;
    mentor: MenteeDashboardMentor | null;
    awaitingReply: boolean;
    messagePreview: { body: string | null } | null;
    checklist: {
        step1: MenteeChecklistStep;
        step2: MenteeChecklistStep;
        step3: MenteeChecklistStep;
    };
    registration: {
        id: number | null;
        status: string | null;
        immutable: boolean;
    } | null;
    upcomingSessions: unknown[];
    upcomingSessionsCollector: string | null;
    myMentorCollector: string | null;
};

export type MenteePairMessage = {
    id: number;
    pairId: number;
    senderRole: string;
    senderId: string | null;
    body: string;
    createdAt: string | null;
};

export type MenteePairMessagesPayload = {
    pairId: number;
    messages: MenteePairMessage[];
    pagination?: {
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
    };
};

export type MenteeMentorCareerTrack = {
    id: number;
    name: string | null;
};

export type MenteeMentorAvailabilitySlot = {
    dayOfWeek: number;
    startMinute: number;
};

export type MenteeMentorProfilePayload = {
    pairId: number;
    mentorUuid: string | null;
    cohort: {
        id: number | null;
        name: string | null;
        startDate: string | null;
        endDate: string | null;
    } | null;
    identity: {
        name: string | null;
        photo: string | null;
        designation: string | null;
        company: string | null;
        location: string | null;
        programName: string | null;
        batchYear: number | null;
        experienceYears: number | null;
        linkedinUrl: string | null;
    };
    bio: string | null;
    specificAreas: string | null;
    careerTracks: MenteeMentorCareerTrack[];
    availability: {
        slotDurationMinutes: number;
        slots: MenteeMentorAvailabilitySlot[];
    };
    hasProfileOverlay: boolean;
    tracksSource: 'preferred' | 'pool' | string;
};

/** Slot state from GET /mentee/booking/slots (DEC-P18-005). */
export type MenteeBookingSlotState = 'available' | 'booked' | 'past';

export type MenteeBookingSlot = {
    date: string;
    startMinute: number;
    durationMinutes: number;
    state: MenteeBookingSlotState;
};

export type MenteeBookingSlotsPayload = {
    month: string;
    pairId: number;
    mentorAlumniId: string | null;
    slotDurationMinutes: number | null;
    slots: MenteeBookingSlot[];
    timezoneLabel: string | null;
};

export type MenteeBookingMode =
    | 'ZOOM'
    | 'GOOGLE_MEET'
    | 'TEAMS'
    | 'GOOGLE_CHAT'
    | 'PHYSICAL_MEET'
    | 'OTHERS';

export type MenteeCreateBookingBody = {
    date: string;
    startMinute: number;
    topic: string;
    agenda?: string | null;
    mode: MenteeBookingMode;
    sessionLink?: string | null;
};

export type MenteeBookingRow = {
    id: number;
    cohortId: number | null;
    pairId: number | null;
    mentorAlumniId: string | null;
    bookedDate: string | null;
    startMinute: number | null;
    durationMinutes: number | null;
    topic: string;
    agenda: string | null;
    mode: string | null;
    sessionLink: string | null;
    bookedByRole: string | null;
    bookedById: string | null;
    status: string | null;
    activeSlotKey: string | null;
    emailResult?: {
        sent?: boolean;
        reason?: string;
        resolved?: boolean;
        recipient?: string;
    } | null;
};

export type MenteeUpcomingBookingsPayload = {
    scope: string;
    pairId: number;
    bookings: MenteeBookingRow[];
};

export type MenteePastSessionRating = {
    id: number | null;
    stars: number | null;
    feedbackText: string | null;
    ratedBy: string | null;
};

export type MenteePastSessionRow = {
    id: number;
    cohortId: number | null;
    pairId: number | null;
    occurredAt: string | null;
    durationMinutes: number | null;
    title: string | null;
    sessionLink: string | null;
    loggedByRole: string | null;
    source: string | null;
    rating: MenteePastSessionRating | null;
};

export type MenteePastSessionsPayload = {
    pairId: number;
    sessions: MenteePastSessionRow[];
};

export type MenteeSessionRatingBody = {
    stars: number;
    feedbackText?: string | null;
};

export type MenteePatchBookingAgendaBody = {
    agenda: string | null;
};

const unwrap = <T>(response: { data?: { data?: T } & T }): T => {
    return (response?.data?.data ?? response?.data) as T;
};

class MenteePortalService {
    static errorMessage(error: { message?: string; data?: { message?: string } } | unknown): string {
        const err = error as { message?: string; data?: { message?: string } };
        return err?.message || err?.data?.message || 'An error occurred while processing your request.';
    }

    static async getRegistration(): Promise<MenteeRegistrationPayload> {
        const response = await axiosClient().get(`${MENTORSHIP_BASE}/mentee/registration`);
        return unwrap<MenteeRegistrationPayload>(response);
    }

    static async saveDraft(body: MenteeRegistrationWriteBody): Promise<MenteeRegistrationPayload> {
        const response = await axiosClient().put(`${MENTORSHIP_BASE}/mentee/registration`, body);
        return unwrap<MenteeRegistrationPayload>(response);
    }

    static async submit(body: MenteeRegistrationWriteBody): Promise<MenteeRegistrationPayload> {
        const response = await axiosClient().post(`${MENTORSHIP_BASE}/mentee/registration/submit`, body);
        return unwrap<MenteeRegistrationPayload>(response);
    }

    static async getDashboard(): Promise<MenteeDashboardPayload> {
        const response = await axiosClient().get(`${MENTORSHIP_BASE}/mentee/dashboard`);
        return unwrap<MenteeDashboardPayload>(response);
    }

    static async getMentorProfile(): Promise<MenteeMentorProfilePayload> {
        const response = await axiosClient().get(`${MENTORSHIP_BASE}/mentee/mentor-profile`);
        return unwrap<MenteeMentorProfilePayload>(response);
    }

    static async listPairMessages(): Promise<MenteePairMessagesPayload> {
        const response = await axiosClient().get(`${MENTORSHIP_BASE}/mentee/pairs/messages`);
        return unwrap<MenteePairMessagesPayload>(response);
    }

    static async sendPairMessage(body: string): Promise<MenteePairMessage> {
        const response = await axiosClient().post(`${MENTORSHIP_BASE}/mentee/pairs/messages`, { body });
        return unwrap<MenteePairMessage>(response);
    }

    /** P18-BATCH-006 — expanded month slots (available | booked | past). */
    static async listBookingSlots(month: string): Promise<MenteeBookingSlotsPayload> {
        const response = await axiosClient().get(`${MENTORSHIP_BASE}/mentee/booking/slots`, {
            params: { month },
        });
        return unwrap<MenteeBookingSlotsPayload>(response);
    }

    /** P18-BATCH-006 — mentee creates a BOOKED row. */
    static async createBooking(body: MenteeCreateBookingBody): Promise<MenteeBookingRow> {
        const response = await axiosClient().post(`${MENTORSHIP_BASE}/mentee/bookings`, body);
        return unwrap<MenteeBookingRow>(response);
    }

    static async listUpcomingBookings(): Promise<MenteeUpcomingBookingsPayload> {
        const response = await axiosClient().get(`${MENTORSHIP_BASE}/mentee/bookings`, {
            params: { scope: 'upcoming' },
        });
        return unwrap<MenteeUpcomingBookingsPayload>(response);
    }

    static async patchBookingAgenda(
        bookingId: number,
        body: MenteePatchBookingAgendaBody
    ): Promise<MenteeBookingRow> {
        const response = await axiosClient().patch(
            `${MENTORSHIP_BASE}/mentee/bookings/${bookingId}`,
            body
        );
        return unwrap<MenteeBookingRow>(response);
    }

    static async listPastSessions(): Promise<MenteePastSessionsPayload> {
        const response = await axiosClient().get(`${MENTORSHIP_BASE}/mentee/sessions`);
        return unwrap<MenteePastSessionsPayload>(response);
    }

    static async createSessionRating(
        sessionId: number,
        body: MenteeSessionRatingBody
    ): Promise<unknown> {
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/mentee/sessions/${sessionId}/rating`,
            body
        );
        return unwrap(response);
    }
}

export default MenteePortalService;
